import React, { memo } from 'react';

import Link from 'next/link';
import { m } from 'framer-motion';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';

import type { Speciality } from '../types';

function SpecialityCard({
  title,
  description,
  image,
  imageObjectPosition,
  relatedConditions = [],
  relatedConditionsLabel,
  button,
}: Speciality): React.ReactElement {
  return (
    <m.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
      whileHover={{
        y: -8,
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -6px rgba(0, 0, 0, 0.08)',
      }}
      className="flex flex-col overflow-hidden rounded-lg border border-slate-100 bg-white shadow-md"
    >
      <Link
        href={button?.link ?? '#'}
        className="relative block h-72 w-full overflow-hidden rounded-lg bg-gray-100"
      >
        <m.div
          className="relative h-full w-full"
          variants={{
            hover: { scale: 1.05 },
          }}
          transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
        >
          <R2Image
            path={image}
            alt={title.content}
            // Card is 1 col below md (768px), 2 cols md-lg, 3 cols at
            // lg+ (see SpecialitiesGrid's grid-cols-1 md:grid-cols-2
            // lg:grid-cols-3) — without this, R2Image's generic default
            // sizes (640px/1024px breakpoints) didn't line up with this
            // grid's actual breakpoints, requesting an oversized image
            // in the 768-1023px range.
            sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
            className="object-cover"
            style={
              imageObjectPosition
                ? { objectPosition: imageObjectPosition }
                : undefined
            }
          />
        </m.div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <RichText styles={title.styles}>{title.content}</RichText>

        {description?.content && (
          <RichText styles={description.styles}>{description.content}</RichText>
        )}

        {relatedConditions.length > 0 && (
          <div className="mt-3">
            {relatedConditionsLabel && (
              <RichText styles={relatedConditionsLabel.styles}>
                {relatedConditionsLabel.content}
              </RichText>
            )}
            <div className="flex flex-wrap gap-2">
              {relatedConditions.map((condition) => (
                <span
                  key={condition}
                  className="bg-border-subtle border-border-neutral-mid text-theme-dark-deep rounded-2xl border px-2 py-1.5 text-[10px] font-bold"
                >
                  {condition}
                </span>
              ))}
            </div>
          </div>
        )}

        {button && (
          <div className="mt-auto pt-5">
            <PrimaryButton
              data={{ content: button.content, link: button.link }}
              paddingClassName="px-5.75 py-2.5"
              widthClassName="w-full"
              className="text-center text-base font-normal"
            />
          </div>
        )}
      </div>
    </m.div>
  );
}

export default memo(SpecialityCard);
